import {zodResolver} from "@hookform/resolvers/zod";
import {Controller, useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {Button, Form, FormGroup, Input, Label, Spinner} from "reactstrap";
import {z} from "zod";
import {useUpdateBusinessDescriptor} from "../../query/companySettingsQueries";

const BUSINESS_DESCRIPTION_MAX_LENGTH = 2000;

const businessDescriptionSchema = z.object({
  businessDescription: z.string().max(BUSINESS_DESCRIPTION_MAX_LENGTH),
});
type BusinessDescriptionFormData = z.infer<typeof businessDescriptionSchema>;

type Props = {
  /** Current value of `businessDescription` (single source of truth, often empty). */
  businessDescription: string | null;
};

/**
 * RDV en ligne — business description editing block (Story 3.3, #550).
 *
 * First and only FE surface that edits `businessDescription`: a textarea bound to
 * the existing `UpdateBusinessDescriptorPayload` / `POST business/descriptor/`.
 * No new field, endpoint or migration — the description is now editable at its
 * natural config point.
 *
 * The save sends ONLY `businessDescription`: every other field in the payload is
 * optional and the backend applies each only when present, so a description edit
 * never touches jobCategory / jobTitle / logo / commercialName (no stale-state
 * clobber from a concurrent edit in the "L'entreprise" tab).
 */
export function BusinessDescriptionSection({businessDescription}: Props) {
  const {updateBusinessDescriptor, isUpdatingBusinessDescriptor} = useUpdateBusinessDescriptor();
  const disabled = isUpdatingBusinessDescriptor;

  const {control, handleSubmit} = useForm<BusinessDescriptionFormData>({
    resolver: zodResolver(businessDescriptionSchema),
    defaultValues: {businessDescription: businessDescription ?? ""},
  });

  return (
    <Form onSubmit={handleSubmit(handleSave)}>
      <h4>Description publique</h4>
      <p className="text-muted small">
        Ce texte décrit votre activité sur votre page de réservation publique.
      </p>

      <FormGroup floating>
        <Controller
          name="businessDescription"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              type="textarea"
              id="field-businessDescription"
              placeholder="Description publique"
              maxLength={BUSINESS_DESCRIPTION_MAX_LENGTH}
              disabled={disabled}
              style={{height: 140}}
            />
          )}
        />
        <Label for="field-businessDescription">Description publique</Label>
      </FormGroup>

      <Button color="primary" type="submit" disabled={disabled}>
        {isUpdatingBusinessDescriptor ? <Spinner size="sm" /> : null}
        {isUpdatingBusinessDescriptor ? " " : ""}Sauvegarder
      </Button>
    </Form>
  );

  async function handleSave(data: BusinessDescriptionFormData) {
    try {
      await updateBusinessDescriptor({businessDescription: data.businessDescription});
      toast.success("Description enregistrée");
    } catch {
      toast.error("Erreur lors de la sauvegarde de la description");
    }
  }
}
